import React from 'react';
import {
  BarChart3, Bell, BookOpen, Bot, Briefcase, CalendarDays, ClipboardCheck, CreditCard,
  FileText, FolderOpen, GitCompare, ShieldCheck, Scale,
  History, LayoutList, LineChart, Mail, Palette, Plug, Radar, Search,
  TrendingUp, User, Users, MessageSquare,
} from 'lucide-react';

/**
 * The one menu, for every shell behind sign-in.
 *
 * There were four of these — the main app, the dashboard, the shared segment
 * shell (startup and government) and the enterprise page — and they had
 * drifted into different products: of roughly eighteen entries between them,
 * only Dashboard, Search and Billing appeared in more than one. Which menu a
 * customer got depended on which screen they happened to be on, so the same
 * account could see Trending and Calendar on one page and Agents and Early
 * Warning on another, with no way to reach the other half.
 *
 * Defining it once is what stops that happening again: a shell may style the
 * list however it likes, but it may not choose its own contents.
 *
 * ``entry`` is the plan-gating key (SRS #140). An entry with one is still
 * shown to everyone — it carries a lock and sells the upgrade — which is why
 * the gate belongs on the item rather than in a filter.
 */
export interface NavLink {
  label: string;
  path: string;
  icon: React.ReactNode;
  /** navigation entry key for plan gating; absent means every plan has it */
  entry?: string;
  /** hidden while an administrator is previewing a customer's account */
  customerOnly?: boolean;
}

export interface NavGroup {
  /** The section heading. Three of them, so an eighteen-item list reads. */
  title: string;
  links: NavLink[];
}

const sz = 17;

export const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Explore',
    links: [
      { label: 'Dashboard', path: '/dashboard', icon: <BarChart3 size={sz} aria-hidden /> },
      { label: 'Search', path: '/search', icon: <Search size={sz} aria-hidden /> },
      // The dashboard's own views are routes, not just tab state: /dashboard
      // syncs the tab from the path, so they belong in the menu like anything
      // else rather than being reachable only from inside one screen.
      { label: 'All Regulations', path: '/dashboard/all-regulations', icon: <LayoutList size={sz} aria-hidden /> },
      { label: 'Trending', path: '/dashboard/trending', icon: <TrendingUp size={sz} aria-hidden /> },
      { label: 'Charts', path: '/dashboard/chart', icon: <LineChart size={sz} aria-hidden /> },
      { label: 'Calendar', path: '/dashboard/calendar', icon: <CalendarDays size={sz} aria-hidden /> },
      { label: 'Newsletters', path: '/dashboard/newsletters', icon: <Mail size={sz} aria-hidden /> },
    ],
  },
  {
    title: 'Work',
    links: [
      { label: 'Chat', path: '/app', icon: <MessageSquare size={sz} aria-hidden /> },
      { label: 'History', path: '/history', icon: <History size={sz} aria-hidden /> },
      { label: 'Alerts', path: '/alerts', icon: <Bell size={sz} aria-hidden />, entry: 'alerts' },
      { label: 'Early Warning', path: '/early-warning', icon: <Radar size={sz} aria-hidden />, entry: 'early_warning' },
      { label: 'Agents', path: '/agents', icon: <Bot size={sz} aria-hidden />, entry: 'agents', customerOnly: true },
      { label: 'Policy Generator', path: '/policy', icon: <FileText size={sz} aria-hidden />, entry: 'policy', customerOnly: true },
      { label: 'Engagements', path: '/matters', icon: <Briefcase size={sz} aria-hidden />, entry: 'matters', customerOnly: true },
      { label: 'Team', path: '/team', icon: <Users size={sz} aria-hidden />, entry: 'team', customerOnly: true },
    ],
  },
  {
    // These existed only in the enterprise shell's own menu, although the
    // routes are top-level and the feature is sold on Business upward — so on
    // every other shell the assessments a customer was paying for could not be
    // reached from the menu at all.
    title: 'Assess',
    links: [
      { label: 'Comparison', path: '/comparison', icon: <GitCompare size={sz} aria-hidden />, entry: 'comparison' },
      { label: 'Governance', path: '/governance', icon: <ShieldCheck size={sz} aria-hidden />, entry: 'governance' },
      { label: 'Risk', path: '/risk', icon: <Scale size={sz} aria-hidden />, entry: 'risk' },
      { label: 'Compliance', path: '/compliance', icon: <ClipboardCheck size={sz} aria-hidden />, entry: 'compliance' },
      { label: 'Repository', path: '/grc/repository', icon: <FolderOpen size={sz} aria-hidden /> },
      { label: 'Company Profile', path: '/grc/company-profile', icon: <Briefcase size={sz} aria-hidden /> },
    ],
  },
  {
    title: 'Account',
    links: [
      { label: 'Billing', path: '/billing', icon: <CreditCard size={sz} aria-hidden />, customerOnly: true },
      { label: 'Notifications', path: '/notifications', icon: <Bell size={sz} aria-hidden />, customerOnly: true },
      { label: 'Integrations', path: '/integrations', icon: <Plug size={sz} aria-hidden />, entry: 'api', customerOnly: true },
      { label: 'Branding', path: '/settings/branding', icon: <Palette size={sz} aria-hidden />, entry: 'branding', customerOnly: true },
      { label: 'Profile', path: '/profile', icon: <User size={sz} aria-hidden />, customerOnly: true },
      { label: 'User Guide', path: '/guide', icon: <BookOpen size={sz} aria-hidden /> },
    ],
  },
];

/** Every link, in order, ignoring the grouping. */
export const ALL_NAV_LINKS: NavLink[] = NAV_GROUPS.flatMap((g) => g.links);

/**
 * "Engagements" is called "Matters" on the plans that sell it that way.
 * The path and the gate are the same; only the word changes (SRS #49/#50).
 */
export function labelFor(link: NavLink, segment?: string | null): string {
  if (link.path === '/matters' && (segment === 'law_firm' || segment === 'enterprise')) return 'Matters';
  return link.label;
}

/** Which menu entry a path belongs to, for the active state. */
export function activePathFor(pathname: string): string | null {
  const exact = ALL_NAV_LINKS.find((l) => l.path === pathname);
  if (exact) return exact.path;
  // /dashboard/<view> before bare /dashboard, longest match first, so
  // /dashboard/trending does not light up Dashboard as well.
  const matches = ALL_NAV_LINKS
    .filter((l) => pathname.startsWith(l.path === '/dashboard' ? '/dashboard' : l.path))
    .sort((a, b) => b.path.length - a.path.length);
  const decoded = decodeURIComponent(pathname);
  const decodedMatch = ALL_NAV_LINKS
    .filter((l) => decoded === l.path || decoded.startsWith(`${l.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return decodedMatch?.path ?? matches[0]?.path ?? null;
}

/**
 * What this workspace is called in the sidebar badge.
 *
 * Lived in the dashboard's own sidebar, so the other three shells either
 * hardcoded their label or had none. Here it is one answer for all of them.
 */
export function workspaceBadgeLabel(planTier?: string | null, organizationType?: string | null): string {
  const tier = (planTier || '').toLowerCase().trim();
  const orgType = (organizationType || '').toLowerCase().trim();

  if (tier === 'startup' || orgType === 'startup') return 'Startup dashboard';
  if (tier === 'enterprise' || orgType === 'corporation') return 'Enterprise dashboard';
  if (tier.startsWith('government') || orgType.includes('government')) return 'Government dashboard';
  if (tier.startsWith('law_firm') || orgType.includes('law firm')) return 'Law firm dashboard';
  if (tier === 'business') return 'Business dashboard';
  if (tier === 'growth') return 'Growth dashboard';
  if (tier === 'professional') return 'Professional dashboard';
  return 'Researchers and academics';
}
